import { useState, useEffect, useCallback } from 'react'
import { useGlobalState } from '@highlight/ipc/react'
import { useHighlightShells } from '@renderer/providers/highlight-shells-provider'
import type { ShellSessionId } from '@shared/types/ShellTypes'
import { TRACKED_CC_SESSION_IDS_KEY } from '@shared/ipc/routers/ClaudeCodeIpc.router'

interface LongRunningActionInput {
  lastInvokedAt: string | null
  resolvedAt: string | null
  outputData: Record<string, unknown> | null | unknown
  conversationId?: string | null
  triggeringLlmMessageId?: string | null
}

interface ChatProgressTarget {
  chatId: string
  scrollToMessageId?: string
  scrollNonce?: number
  instantScrollToBottom?: boolean
}

interface LongRunningActionOptions {
  openChat?: (target: ChatProgressTarget) => void
}

interface LongRunningActionResult {
  isLongRunning: boolean
  isResolved: boolean
  showViewProgress: boolean
  viewProgressLabel: string
  alternateLabel: string
  elapsed: string | null
  finalMessage: string | undefined
  openProgress: () => void
}

let chatProgressRequestCounter = 0

/** Extract the shell session ID from an action's outputData, if present. */
export function getShellSessionId(outputData: Record<string, unknown> | null | unknown): string | undefined {
  return (outputData as Record<string, unknown> | null)?.shellSessionId as string | undefined
}

function getClaudeCodeSessionId(outputData: Record<string, unknown> | null | unknown): string | undefined {
  return (outputData as Record<string, unknown> | null)?.claudeCodeSessionId as string | undefined
}

function getExternalSession(outputData: Record<string, unknown> | null | unknown):
  | {
      type: 'claude'
      sessionId: string
    }
  | undefined {
  const isExternal = ((outputData as Record<string, unknown> | null)?.external as boolean | undefined) ?? false
  if (!isExternal) return undefined

  if (getClaudeCodeSessionId(outputData)) {
    return {
      type: 'claude',
      sessionId: getClaudeCodeSessionId(outputData) as string,
    }
  }

  return undefined
}

/**
 * An approved auto-mode "plan" action carries `{ autoMode: grant }` in its
 * outputData (set by the backend when the user approves it). It runs
 * asynchronously in the originating chat, so it gets the long-running UX —
 * "In Progress" until the grant clears, then "Completed".
 */
export function isAutoModePlanAction(action: Pick<LongRunningActionInput, 'outputData'>): boolean {
  return !!(action.outputData as Record<string, unknown> | null)?.autoMode
}

export function isLongRunningAction(action: Pick<LongRunningActionInput, 'outputData'>): boolean {
  return (
    !!getShellSessionId(action.outputData) || !!getExternalSession(action.outputData) || isAutoModePlanAction(action)
  )
}

/**
 * The text the plan's run signed off with, merged into outputData when the
 * backend resolves the action. Absent while the plan is still running, and for
 * runs that ended without the model saying anything.
 */
export function getAutoModePlanFinalMessage(action: Pick<LongRunningActionInput, 'outputData'>): string | undefined {
  const finalMessage = (action.outputData as Record<string, unknown> | null)?.finalMessage
  return typeof finalMessage === 'string' && finalMessage.trim() ? finalMessage : undefined
}

function formatChatStyleElapsed(startDate: string | Date, endDate?: string | Date | null): string | null {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate
  const end = endDate ? (typeof endDate === 'string' ? new Date(endDate) : endDate) : new Date()
  const elapsedMs = Math.max(0, end.getTime() - start.getTime())
  const s = elapsedMs / 1000
  if (s < 60) return `${s.toFixed(1)}s`
  const m = Math.floor(s / 60)
  const rem = Math.floor(s % 60)
  return `${m}m ${rem}s`
}

// A single 1 Hz ticker shared by every running elapsed-time label, rather than
// one setInterval per cell. The interval only exists while something is
// subscribed, so it stops entirely when no actions are in progress.
const tickSubscribers = new Set<() => void>()
let tickInterval: ReturnType<typeof setInterval> | null = null

function subscribeSecondTick(callback: () => void): () => void {
  tickSubscribers.add(callback)
  if (tickInterval === null) {
    tickInterval = setInterval(() => {
      for (const subscriber of tickSubscribers) subscriber()
    }, 1000)
  }
  return () => {
    tickSubscribers.delete(callback)
    if (tickSubscribers.size === 0 && tickInterval !== null) {
      clearInterval(tickInterval)
      tickInterval = null
    }
  }
}

function useElapsedTime(startDate: string | null, endDate: string | null): string | null {
  const [elapsed, setElapsed] = useState(() => {
    if (!startDate) return null
    return formatChatStyleElapsed(startDate, endDate)
  })

  useEffect(() => {
    if (!startDate) {
      setElapsed(null)
      return
    }
    if (endDate) {
      setElapsed(formatChatStyleElapsed(startDate, endDate))
      return
    }
    setElapsed(formatChatStyleElapsed(startDate))
    return subscribeSecondTick(() => setElapsed(formatChatStyleElapsed(startDate)))
  }, [startDate, endDate])

  return elapsed
}

export function useLongRunningAction(
  action: LongRunningActionInput,
  options: LongRunningActionOptions = {},
): LongRunningActionResult {
  const { shells, openShell } = useHighlightShells()
  const { openChat } = options
  const [trackedClaudeSessionIds] = useGlobalState<string[]>(TRACKED_CC_SESSION_IDS_KEY, [])

  const shellSessionId = getShellSessionId(action.outputData)
  const externalSession = getExternalSession(action.outputData)
  const isAutoModePlan = isAutoModePlanAction(action)

  // long running is not determined by shell alive status, but by the presence of a shell session ID or external session
  const isLongRunning = isLongRunningAction(action)

  const isResolved = !!action.resolvedAt
  const elapsed = useElapsedTime(action.lastInvokedAt, action.resolvedAt)

  const shellAlive = shellSessionId ? shells.some((s) => s.id === shellSessionId) : false
  const sessionTracked = externalSession ? trackedClaudeSessionIds.includes(externalSession.sessionId) : false

  const progressConversationId = action.originConversationId ?? action.conversationId
  const progressMessageId = action.conversationId === progressConversationId ? action.triggeringLlmMessageId : undefined
  const showViewProgress = isAutoModePlan ? !!progressConversationId && !!openChat : shellAlive
  const viewProgressLabel = isResolved ? 'View Results' : 'View Progress'
  const alternateLabel = sessionTracked ? 'Running Externally' : 'Cannot view progress'

  const openProgress = useCallback(() => {
    if (isAutoModePlan && progressConversationId && openChat) {
      const scrollNonce = ++chatProgressRequestCounter
      openChat(
        progressMessageId
          ? { chatId: progressConversationId, scrollToMessageId: progressMessageId, scrollNonce }
          : { chatId: progressConversationId, instantScrollToBottom: true, scrollNonce },
      )
      return
    }
    if (shellSessionId) {
      openShell(shellSessionId as ShellSessionId)
    }
  }, [isAutoModePlan, openChat, openShell, progressConversationId, progressMessageId, shellSessionId])

  return {
    isLongRunning,
    isResolved,
    showViewProgress,
    viewProgressLabel,
    alternateLabel,
    elapsed,
    finalMessage: isAutoModePlan ? getAutoModePlanFinalMessage(action) : undefined,
    openProgress,
  }
}
